import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Modal } from '../../../components/ui/Modal'
import { Alert } from '../../../components/ui/Alert'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { Pagination } from '../../../components/ui/Pagination'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { useAtiradores } from '../../atiradores/hooks/useAtiradores'
import { useDebounce } from '../../../hooks/useDebounce'
import { useModalidades } from '../../modalidades/hooks/useModalidades'
import { useEtapas } from '../../etapas/hooks/useEtapas'
import { useArmas } from '../../armas/hooks/useArmas'
import { atiradorService } from '../../atiradores/services/atiradores.service'
import { useInscricoes, useCancelarInscricao, useTrocarModalidadeInscricao, useExcluirInscricao } from '../hooks/useInscricoes'
import { api } from '../../../api/axios'
import { printComprovanteInscricaoThermal } from '../../../lib/printThermal'
import type { Atirador } from '../../atiradores/types'
import type { Inscricao } from '../types'

// ── Types ─────────────────────────────────────────────────────────────────────

interface RegraValor {
  operador: string
  quantidade: number
  valor: number
  ordem_aplicacao: number
}

interface RegraValoresResponse {
  numero_max_inscricao: number
  regras: RegraValor[]
}

interface ModalidadePublica {
  idmodalidade: number
  nome: string
  grupo: string
  divisao: string | null
  permite_reinscricao: boolean
  max_reinscricao: number
}

interface EtapaPublica {
  idetapa: number
  idmodalidade: number
  titulo: string
  titulo_reduzido: string
  numero_etapa: number
  data_inicio: string
  data_encerramento: string
  data_encerramento_inscricoes: string
  modalidade_nome: string
  campeonato_descricao: string
}

interface EtapaRound {
  numero_etapa: number
  titulo: string
  data_encerramento_min: string
  modalidades_abertas: number
}

interface ModalidadeComEtapa extends ModalidadePublica {
  etapaAberta: EtapaPublica
}

interface BatchResult {
  inscricoes: Array<{
    idmodalidade: number
    modalidade_nome: string
    quantidade: number
    subtotal: number
  }>
  total: number
}

type PaymentMode = 'none' | 'pix' | 'pos' | 'isento'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCurrency(val: number | string | null | undefined): string {
  if (val === null || val === undefined) return 'R$ —'
  const n = typeof val === 'string' ? parseFloat(val) : val
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n)
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso.includes('T') ? iso : iso + 'T12:00:00').toLocaleDateString('pt-BR')
}

function calcularValorInscricao(numero: number, regras: RegraValor[]): number {
  for (const regra of regras) {
    let match = false
    switch (regra.operador) {
      case '=':  match = numero === regra.quantidade; break
      case '>':  match = numero > regra.quantidade;   break
      case '>=': match = numero >= regra.quantidade;  break
      case '<':  match = numero < regra.quantidade;   break
      case '<=': match = numero <= regra.quantidade;  break
    }
    if (match) return Number(regra.valor)
  }
  return 0
}

function calcularSubtotal(quantidade: number, regras: RegraValor[], offset = 0): number {
  let total = 0
  for (let i = 1; i <= quantidade; i++) total += calcularValorInscricao(offset + i, regras)
  return total
}

const STATUS_BADGE: Record<string, string> = {
  ativa: 'bg-green-100 text-green-700',
  cancelada: 'bg-red-100 text-red-700',
  finalizada: 'bg-compumaq-pretoSuave text-compumaq-textoSuave',
  confirmada: 'bg-compumaq-dourado/15 text-compumaq-doradoClaro',
  pendente_pagamento: 'bg-yellow-100 text-yellow-700',
}

// ── Componente principal ──────────────────────────────────────────────────────

export function InscricoesPage() {
  // Filtros da listagem
  const [filterCampeonato, setFilterCampeonato] = useState<number | ''>('')
  const [filterModalidade, setFilterModalidade] = useState<number | ''>('')
  const [filterEtapa, setFilterEtapa] = useState<number | ''>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [filterAtirador, setFilterAtirador] = useState<number | ''>('')
  const [filtroAtiradorSearch, setFiltroAtiradorSearch] = useState('')
  const [filtroShowSuggestions, setFiltroShowSuggestions] = useState(false)
  const atiradorAutocompleteRef = useRef<HTMLDivElement>(null)
  const [page, setPage] = useState(1)

  // Estado do modal de nova inscrição
  const [modalOpen, setModalOpen] = useState(false)
  const [modalStep, setModalStep] = useState<'form' | 'payment' | 'sucesso'>('form')

  // Seleções em cascata dentro do modal
  const [selCampeonato, setSelCampeonato] = useState<number | ''>('')
  const [selNumeroEtapa, setSelNumeroEtapa] = useState<number | null>(null)

  // Atirador (autocomplete)
  const [atiradorSearch, setAtiradorSearch] = useState('')
  const [atiradorSuggestions, setAtiradorSuggestions] = useState<Atirador[]>([])
  const [atiradorLoading, setAtiradorLoading] = useState(false)
  const [selectedAtirador, setSelectedAtirador] = useState<Atirador | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const autocompleteRef = useRef<HTMLDivElement>(null)

  // Outros campos do form
  const [categoria, setCategoria] = useState<'overall' | 'dama'>('overall')
  const [selArma, setSelArma] = useState<number | ''>('')

  // Multi-modalidade
  const [quantidades, setQuantidades] = useState<Record<number, number>>({})
  const [regraValores, setRegraValores] = useState<RegraValoresResponse | null>(null)
  const [modalidadesPublicas, setModalidadesPublicas] = useState<ModalidadePublica[]>([])
  const [etapasPublicas, setEtapasPublicas] = useState<EtapaPublica[]>([])
  const [loadingDados, setLoadingDados] = useState(false)

  // Pagamento
  const [paymentMode, setPaymentMode] = useState<PaymentMode>('none')
  const [batchResult, setBatchResult] = useState<BatchResult | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Cancelar inscricao
  const [cancelTarget, setCancelTarget] = useState<Inscricao | null>(null)

  // Confirmar pagamento (admin)
  const [confirmPagamentoTarget, setConfirmPagamentoTarget] = useState<Inscricao | null>(null)
  const [confirmPagamentoForma, setConfirmPagamentoForma] = useState<'pix' | 'cartao' | 'pos' | 'isento'>('pix')
  const [confirmPagamentoLoading, setConfirmPagamentoLoading] = useState(false)

  // Alertas
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  // Trocar modalidade
  const [trocarTarget, setTrocarTarget] = useState<Inscricao | null>(null)
  const [trocarModalidade, setTrocarModalidade] = useState<number | ''>('')
  const [trocarEtapa, setTrocarEtapa] = useState<number | ''>('')

  // ── Queries de listagem ───────────────────────────────────────────────────
  const listFilters = {
    ...(filterCampeonato ? { idcampeonato: filterCampeonato as number } : {}),
    ...(filterModalidade ? { idmodalidade: filterModalidade as number } : {}),
    ...(filterEtapa ? { numero_etapa: filterEtapa as number } : {}),
    ...(filterStatus ? { status: filterStatus as 'ativa' | 'cancelada' | 'finalizada' | 'confirmada' | 'pendente_pagamento' } : {}),
    ...(filterAtirador ? { idatirador: filterAtirador as number } : {}),
    page,
    limit: 20,
  }

  const debouncedAtiradorSearch = useDebounce(filtroAtiradorSearch, 400)
  const { data: atiradoresBuscaData, isLoading: atiradoresBuscando } = useAtiradores(
    debouncedAtiradorSearch.length >= 3 ? { nome: debouncedAtiradorSearch, limit: 10 } : {},
  )
  const atiradoresBusca = atiradoresBuscaData?.data ?? []
  const { data: inscricoesData, isLoading } = useInscricoes(listFilters)
  const cancelarInscricao = useCancelarInscricao()
  const trocarModalidadeMutation = useTrocarModalidadeInscricao()
  const excluirMutation = useExcluirInscricao()
  const queryClient = useQueryClient()

  // Dados para modal de troca de modalidade
  const { data: modalidadesTrocaData } = useModalidades(
    trocarTarget ? { idcampeonato: trocarTarget.idcampeonato, limit: 100 } : {},
  )
  const modalidadesTroca = modalidadesTrocaData?.data ?? []

  const { data: etapasTrocaData } = useEtapas(
    trocarTarget && trocarModalidade
      ? { idcampeonato: trocarTarget.idcampeonato, idmodalidade: trocarModalidade as number, limit: 50 }
      : {},
  )
  const etapasTroca = etapasTrocaData?.data ?? []

  // ── Queries do modal ──────────────────────────────────────────────────────
  const { data: campeonatosData } = useCampeonatos({ limit: 200 })

  // Armas do atirador selecionado
  const { data: armasData } = useArmas(
    selectedAtirador ? { idatirador: selectedAtirador.idatirador, limit: 100 } : {},
  )
  const armas = selectedAtirador ? (armasData?.data ?? []) : []

  // Campeonato selecionado completo (para saber se obriga arma)
  const campeonatoSelecionado = campeonatosData?.data?.find(c => c.idcampeonato === selCampeonato)

  // Rounds de etapa abertas (agrupados por numero_etapa)
  const etapaRounds: EtapaRound[] = (() => {
    if (!etapasPublicas.length) return []
    const now = new Date()
    const groups: Record<number, EtapaPublica[]> = {}
    for (const e of etapasPublicas) {
      if (!groups[e.numero_etapa]) groups[e.numero_etapa] = []
      groups[e.numero_etapa].push(e)
    }
    return Object.entries(groups)
      .map(([num, etps]) => {
        const abertas = etps.filter(e => new Date(e.data_encerramento_inscricoes) > now)
        const datas = etps.map(e => e.data_encerramento_inscricoes).sort()
        return {
          numero_etapa: parseInt(num, 10),
          titulo: `Etapa ${num}`,
          data_encerramento_min: datas[0],
          modalidades_abertas: abertas.length,
        }
      })
      .filter(r => r.modalidades_abertas > 0)
      .sort((a, b) => a.numero_etapa - b.numero_etapa)
  })()

  // Modalidades com etapa aberta para o numero_etapa selecionado
  const modalidadesComEtapa: ModalidadeComEtapa[] = (() => {
    if (!selNumeroEtapa || !modalidadesPublicas.length) return []
    const now = new Date()
    const result: ModalidadeComEtapa[] = []
    for (const m of modalidadesPublicas) {
      const etapa = etapasPublicas.find(
        e => e.idmodalidade === m.idmodalidade &&
             e.numero_etapa === selNumeroEtapa &&
             new Date(e.data_encerramento_inscricoes) > now,
      )
      if (etapa) result.push({ ...m, etapaAberta: etapa })
    }
    return result
  })()

  // Subtotais e total
  const { subtotaisGlobais, totalGeral } = (() => {
    const subtotais: Record<number, number> = {}
    if (!regraValores) return { subtotaisGlobais: subtotais, totalGeral: 0 }
    let globalOffset = 0
    let total = 0
    for (const m of modalidadesComEtapa) {
      const qty = quantidades[m.idmodalidade] ?? 0
      if (qty === 0) { subtotais[m.idmodalidade] = 0; continue }
      const sub = calcularSubtotal(qty, regraValores.regras, globalOffset)
      subtotais[m.idmodalidade] = sub
      globalOffset += qty
      total += sub
    }
    return { subtotaisGlobais: subtotais, totalGeral: total }
  })()

  const temSelecionado = Object.values(quantidades).some(q => q > 0)

  // ── Carregar dados quando campeonato muda ─────────────────────────────────
  useEffect(() => {
    if (!selCampeonato) {
      setModalidadesPublicas([])
      setEtapasPublicas([])
      setRegraValores(null)
      setSelNumeroEtapa(null)
      setQuantidades({})
      return
    }
    setLoadingDados(true)
    Promise.all([
      api.get<{ data: ModalidadePublica[] }>('/publico/modalidades', { params: { idcampeonato: selCampeonato } }).then(r => r.data.data),
      api.get<{ data: EtapaPublica[] }>('/publico/etapas', { params: { idcampeonato: selCampeonato } }).then(r => r.data.data),
      api.get<RegraValoresResponse>(`/publico/campeonatos/${selCampeonato}/regra-valores`).then(r => r.data),
    ]).then(([mods, etps, regras]) => {
      setModalidadesPublicas(mods)
      setEtapasPublicas(etps)
      setRegraValores(regras)
    }).catch(() => {
      setAlert({ type: 'error', message: 'Erro ao carregar dados do campeonato.' })
    }).finally(() => setLoadingDados(false))
  }, [selCampeonato])

  // Reset quando numero_etapa muda
  useEffect(() => { setQuantidades({}) }, [selNumeroEtapa])

  // ── Autocomplete de atirador ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(e.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    if (atiradorSearch.length < 2) {
      setAtiradorSuggestions([])
      return
    }
    const timer = setTimeout(async () => {
      setAtiradorLoading(true)
      try {
        const isCpf = /[\d./-]/.test(atiradorSearch) && atiradorSearch.replace(/\D/g, '').length >= 3
        const filters = isCpf ? { cpf: atiradorSearch, limit: 10 } : { nome: atiradorSearch, limit: 10 }
        const result = await atiradorService.list(filters)
        setAtiradorSuggestions(result.data)
        setShowSuggestions(true)
      } catch {
        setAtiradorSuggestions([])
      } finally {
        setAtiradorLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [atiradorSearch])

  function selectAtirador(a: Atirador) {
    setSelectedAtirador(a)
    setAtiradorSearch(a.nome)
    setShowSuggestions(false)
    setSelArma('')
  }

  function getMaxQty(m: ModalidadePublica): number {
    if (!regraValores) return 0
    const maxInscricao = regraValores.numero_max_inscricao
    if (!m.permite_reinscricao) return 1
    const maxMod = m.max_reinscricao > 0 ? m.max_reinscricao : maxInscricao
    return Math.min(maxInscricao, maxMod)
  }

  function adjustQty(idmodalidade: number, delta: number, maxQty: number) {
    setQuantidades(prev => {
      const current = prev[idmodalidade] ?? 0
      const next = Math.max(0, Math.min(maxQty, current + delta))
      return { ...prev, [idmodalidade]: next }
    })
  }

  // ── Abrir modal ───────────────────────────────────────────────────────────
  function openModal() {
    setModalStep('form')
    setSelCampeonato('')
    setSelNumeroEtapa(null)
    setSelectedAtirador(null)
    setAtiradorSearch('')
    setCategoria('overall')
    setSelArma('')
    setQuantidades({})
    setPaymentMode('none')
    setBatchResult(null)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
  }

  // ── Avançar para pagamento ────────────────────────────────────────────────
  function handleAvancarPagamento() {
    if (!selectedAtirador || selCampeonato === '' || !selNumeroEtapa) {
      setAlert({ type: 'error', message: 'Preencha todos os campos obrigatorios' })
      return
    }
    if (!temSelecionado) {
      setAlert({ type: 'error', message: 'Selecione pelo menos uma modalidade' })
      return
    }
    if (campeonatoSelecionado?.flag_obriga_arma_inscricao && selArma === '') {
      setAlert({ type: 'error', message: 'Arma obrigatoria para este campeonato' })
      return
    }
    setPaymentMode('none')
    setModalStep('payment')
  }

  // ── Finalizar inscrição via batch ─────────────────────────────────────────
  async function handleFinalizar(forma_pagamento: 'pix' | 'pos' | 'isento') {
    if (!selectedAtirador || selCampeonato === '' || !selNumeroEtapa) return
    const inscricoes = Object.entries(quantidades)
      .filter(([, qty]) => qty > 0)
      .map(([idmod, qty]) => ({ idmodalidade: parseInt(idmod, 10), quantidade: qty }))
    if (inscricoes.length === 0) return

    setSubmitting(true)
    try {
      const resp = await api.post<{ data: BatchResult }>('/publico/inscricoes-batch', {
        idatirador: selectedAtirador.idatirador,
        idcampeonato: selCampeonato as number,
        numero_etapa: selNumeroEtapa,
        inscricoes,
        pagar: true,
        forma_pagamento,
      })
      setBatchResult(resp.data.data)
      setModalStep('sucesso')
      // Invalidar listagem
      window.dispatchEvent(new CustomEvent('inscricoes-updated'))
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao criar inscricao' })
    } finally {
      setSubmitting(false)
    }
  }

  // ── Imprimir comprovante (apenas atirador atual) ──────────────────────────
  function imprimirComprovante() {
    if (!batchResult || !selectedAtirador) return
    const campDesc = campeonatoSelecionado?.descricao ?? ''
    const etapaNum = selNumeroEtapa ?? ''
    const linhasModalidades = batchResult.inscricoes.map(insc =>
      `<tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${insc.modalidade_nome}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:center;">${insc.quantidade}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${formatCurrency(insc.subtotal)}</td>
      </tr>`
    ).join('')
    const formaPagto = paymentMode === 'pos' ? 'Cartao POS' : paymentMode === 'isento' ? 'Isento' : 'PIX'
    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Comprovante de Inscricao</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 13px; color: #111; margin: 32px; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    .subtitle { color: #555; font-size: 12px; margin-bottom: 20px; }
    .info { margin-bottom: 16px; }
    .info-row { display: flex; gap: 16px; margin-bottom: 6px; }
    .label { font-weight: bold; min-width: 100px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    thead { background: #f3f4f6; }
    th { padding: 8px; text-align: left; font-size: 12px; border-bottom: 2px solid #d1d5db; }
    .total-row td { font-weight: bold; padding-top: 10px; font-size: 14px; }
    .footer { margin-top: 24px; font-size: 11px; color: #888; border-top: 1px solid #e5e7eb; padding-top: 10px; }
  </style>
</head>
<body>
  <h1>Comprovante de Inscricao</h1>
  <div class="subtitle">Campeonato de Tiro</div>
  <div class="info">
    <div class="info-row"><span class="label">Atirador:</span><span>${selectedAtirador.nome}</span></div>
    <div class="info-row"><span class="label">CPF:</span><span>${selectedAtirador.cpf ?? ''}</span></div>
    <div class="info-row"><span class="label">Campeonato:</span><span>${campDesc}</span></div>
    <div class="info-row"><span class="label">Etapa:</span><span>${etapaNum}</span></div>
    <div class="info-row"><span class="label">Pagamento:</span><span>${formaPagto}</span></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Modalidade</th>
        <th style="text-align:center;">Qtd</th>
        <th style="text-align:right;">Valor</th>
      </tr>
    </thead>
    <tbody>
      ${linhasModalidades}
      <tr class="total-row">
        <td colspan="2" style="padding:10px 8px 0;">Total</td>
        <td style="padding:10px 8px 0;text-align:right;">${formatCurrency(batchResult.total)}</td>
      </tr>
    </tbody>
  </table>
  <div class="footer">Emitido em ${new Date().toLocaleString('pt-BR')}</div>
</body>
</html>`
    const win = window.open('', '_blank', 'width=700,height=600')
    if (win) {
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => win.print(), 400)
    }
  }

  // ── Imprimir comprovante individual (a partir da listagem) ───────────────
  function imprimirComprovanteIndividual(inscricao: Inscricao) {
    const formaPagto = inscricao.forma_pagamento
      ? (inscricao.forma_pagamento === 'pos' ? 'Cartao POS' : inscricao.forma_pagamento === 'isento' ? 'Isento' : inscricao.forma_pagamento === 'cartao' ? 'Cartao de Credito' : 'PIX')
      : '—'
    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Comprovante de Inscricao</title>
  <style>
    body { font-family: Arial, sans-serif; font-size: 13px; color: #111; margin: 32px; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    .subtitle { color: #555; font-size: 12px; margin-bottom: 20px; }
    .info { margin-bottom: 16px; }
    .info-row { display: flex; gap: 16px; margin-bottom: 6px; }
    .label { font-weight: bold; min-width: 100px; }
    table { width: 100%; border-collapse: collapse; margin-top: 12px; }
    thead { background: #f3f4f6; }
    th { padding: 8px; text-align: left; font-size: 12px; border-bottom: 2px solid #d1d5db; }
    .total-row td { font-weight: bold; padding-top: 10px; font-size: 14px; }
    .footer { margin-top: 24px; font-size: 11px; color: #888; border-top: 1px solid #e5e7eb; padding-top: 10px; }
  </style>
</head>
<body>
  <h1>Comprovante de Inscricao</h1>
  <div class="subtitle">Campeonato de Tiro</div>
  <div class="info">
    <div class="info-row"><span class="label">Atirador:</span><span>${inscricao.atirador_nome ?? ''}</span></div>
    <div class="info-row"><span class="label">CPF:</span><span>${inscricao.atirador_cpf ?? ''}</span></div>
    <div class="info-row"><span class="label">Clube:</span><span>${inscricao.clube_nome ?? ''}</span></div>
    <div class="info-row"><span class="label">Campeonato:</span><span>${inscricao.campeonato_descricao ?? ''}</span></div>
    <div class="info-row"><span class="label">Etapa:</span><span>${inscricao.etapa_titulo ?? ''}</span></div>
    <div class="info-row"><span class="label">Modalidade:</span><span>${inscricao.modalidade_nome ?? ''}</span></div>
    <div class="info-row"><span class="label">Inscricao:</span><span>#${inscricao.numero_inscricao}</span></div>
    <div class="info-row"><span class="label">Categoria:</span><span>${inscricao.categoria}</span></div>
    <div class="info-row"><span class="label">Pagamento:</span><span>${formaPagto}</span></div>
    <div class="info-row"><span class="label">Status:</span><span>${inscricao.status}</span></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Item</th>
        <th style="text-align:right;">Valor</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">${inscricao.modalidade_nome ?? 'Inscricao'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;">${formatCurrency(inscricao.valor_cobrado)}</td>
      </tr>
      <tr class="total-row">
        <td style="padding:10px 8px 0;">Total</td>
        <td style="padding:10px 8px 0;text-align:right;">${formatCurrency(inscricao.valor_cobrado)}</td>
      </tr>
    </tbody>
  </table>
  <div class="footer">Emitido em ${new Date().toLocaleString('pt-BR')}</div>
</body>
</html>`
    const win = window.open('', '_blank', 'width=700,height=600')
    if (win) {
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => win.print(), 400)
    }
  }

  // ── Imprimir comprovante individual (formato térmico Bluetooth) ────────
  function imprimirComprovanteThermal(inscricao: Inscricao) {
    printComprovanteInscricaoThermal({
      atirador_nome: inscricao.atirador_nome ?? '',
      atirador_cpf: inscricao.atirador_cpf ?? '',
      clube_nome: inscricao.clube_nome ?? '',
      campeonato_descricao: inscricao.campeonato_descricao ?? '',
      etapa_titulo: inscricao.etapa_titulo ?? '',
      modalidade_nome: inscricao.modalidade_nome ?? '',
      numero_inscricao: inscricao.numero_inscricao,
      categoria: inscricao.categoria,
      valor_cobrado: inscricao.valor_cobrado,
      forma_pagamento: inscricao.forma_pagamento,
      status: inscricao.status,
    })
  }

  // ── Cancelar inscrição ────────────────────────────────────────────────────
  async function handleCancelar() {
    if (!cancelTarget) return
    try {
      await cancelarInscricao.mutateAsync(cancelTarget.idinscricao)
      setAlert({ type: 'success', message: 'Inscricao cancelada com sucesso' })
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao cancelar inscricao' })
    } finally {
      setCancelTarget(null)
    }
  }

  // Invalidar listagem ao finalizar inscrição
  useEffect(() => {
    const handler = () => {
      // forcar refetch usando key change
    }
    window.addEventListener('inscricoes-updated', handler)
    return () => window.removeEventListener('inscricoes-updated', handler)
  }, [])

  // Fechar dropdown de atirador ao clicar fora
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (atiradorAutocompleteRef.current && !atiradorAutocompleteRef.current.contains(e.target as Node)) {
        setFiltroShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  async function handleConfirmarPagamento() {
    if (!confirmPagamentoTarget) return
    setConfirmPagamentoLoading(true)
    try {
      await api.put(`/inscricoes/${confirmPagamentoTarget.idinscricao}/confirmar-pagamento`, {
        forma_pagamento: confirmPagamentoForma,
      })
      setAlert({ type: 'success', message: `Pagamento confirmado via ${confirmPagamentoForma} com sucesso!` })
      queryClient.invalidateQueries({ queryKey: ['inscricoes'] })
      setConfirmPagamentoTarget(null)
      setConfirmPagamentoForma('pix')
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao confirmar pagamento' })
    } finally {
      setConfirmPagamentoLoading(false)
    }
  }

  const meta = inscricoesData?.meta

  const podeAvancar =
    selectedAtirador !== null &&
    selCampeonato !== '' &&
    selNumeroEtapa !== null &&
    temSelecionado &&
    !(campeonatoSelecionado?.flag_obriga_arma_inscricao && selArma === '')

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <PageHeader
        title="Inscricoes"
        description="Gerenciar inscricoes nos campeonatos"
        action={
          <button
            onClick={openModal}
            className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro"
          >
            + Nova Inscricao
          </button>
        }
      />

      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {/* Filtros da listagem */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
            <select
              className={selectClass}
              value={filterCampeonato}
              onChange={e => { setFilterCampeonato(e.target.value ? parseInt(e.target.value) : ''); setPage(1) }}
            >
              <option value="">Todos</option>
              {campeonatosData?.data?.map(c => (
                <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao}</option>
              ))}
            </select>
          </div>
          <div className="w-40">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Etapa</label>
            <select
              className={selectClass}
              value={filterEtapa}
              onChange={e => { setFilterEtapa(e.target.value ? parseInt(e.target.value) : ''); setPage(1) }}
            >
              <option value="">Todas as etapas</option>
              {[1, 2, 3, 4, 5, 6].map(n => (
                <option key={n} value={n}>{n}ª Etapa</option>
              ))}
            </select>
          </div>
          <div className="w-40">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Status</label>
            <select
              className={selectClass}
              value={filterStatus}
              onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
            >
              <option value="">Todos</option>
              <option value="ativa">Ativa</option>
              <option value="confirmada">Confirmada</option>
              <option value="cancelada">Cancelada</option>
              <option value="finalizada">Finalizada</option>
              <option value="pendente_pagamento">Pendente Pagamento</option>
            </select>
          </div>

          {/* Filtro por atirador */}
          <div ref={atiradorAutocompleteRef} className="flex-1 min-w-[220px] relative">
            <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Atirador</label>
            {filterAtirador ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                  {atiradoresBusca.find(a => a.idatirador === filterAtirador)?.nome || `Atirador #${filterAtirador}`}
                </div>
                <button
                  onClick={() => { setFilterAtirador(''); setFiltroAtiradorSearch(''); setPage(1) }}
                  className="px-3 py-2 text-sm text-red-600 hover:text-red-800 border border-compumaq-dourado/25 rounded-lg hover:bg-compumaq-preto"
                >
                  Limpar
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Digite o nome do atirador..."
                  className={inputClass}
                  value={filtroAtiradorSearch}
                  onChange={e => {
                    setFiltroAtiradorSearch(e.target.value)
                    setFiltroShowSuggestions(true)
                  }}
                  onFocus={() => setFiltroShowSuggestions(true)}
                />
                {atiradoresBuscando && (
                  <div className="absolute right-3 top-2.5">
                    <Spinner size="sm" />
                  </div>
                )}
                {filtroShowSuggestions && atiradoresBusca.length > 0 && (
                  <div className="absolute z-10 w-full bg-compumaq-pretoSuave border border-compumaq-dourado/25 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {atiradoresBusca.map(a => (
                      <button
                        key={a.idatirador}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-compumaq-preto border-b border-compumaq-dourado/25 last:border-0"
                        onClick={() => {
                          setFilterAtirador(a.idatirador)
                          setFiltroAtiradorSearch(a.nome)
                          setFiltroShowSuggestions(false)
                          setPage(1)
                        }}
                      >
                        <span className="font-medium">{a.nome}</span>
                        <span className="text-compumaq-textoSuave text-xs ml-2">{a.cpf}</span>
                      </button>
                    ))}
                  </div>
                )}
                {filtroShowSuggestions && debouncedAtiradorSearch.length >= 3 && atiradoresBusca.length === 0 && (
                  <div className="absolute z-10 w-full bg-compumaq-pretoSuave border border-compumaq-dourado/25 rounded-lg shadow-lg mt-1 px-3 py-2 text-sm text-compumaq-textoSuave">
                    Nenhum atirador encontrado
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => { setFilterCampeonato(''); setFilterModalidade(''); setFilterEtapa(''); setFilterStatus(''); setFilterAtirador(''); setFiltroAtiradorSearch(''); setPage(1) }}
              className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Tabela de inscrições */}
      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : !inscricoesData?.data?.length ? (
          <div className="text-center py-16 text-compumaq-textoSuave">
            <p className="text-4xl mb-3">📋</p>
            <p className="font-medium">Nenhuma inscricao encontrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Atirador</th>
                    <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Campeonato</th>
                    <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Modalidade</th>
                    <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Etapa</th>
                    <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Cat.</th>
                    <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Insc #</th>
                    <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Valor</th>
                    <th className="text-left px-4 py-3 font-medium text-compumaq-textoSuave">Status</th>
                    <th className="text-right px-4 py-3 font-medium text-compumaq-textoSuave">Acoes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-compumaq-dourado/25">
                  {inscricoesData.data.map(i => (
                    <tr key={i.idinscricao} className="hover:bg-compumaq-preto">
                      <td className="px-4 py-3">
                        <p className="font-medium text-compumaq-texto">{i.atirador_nome ?? '—'}</p>
                        <p className="text-xs text-compumaq-textoSuave font-mono">{i.atirador_cpf ?? ''}</p>
                      </td>
                      <td className="px-4 py-3 text-compumaq-texto text-xs max-w-[140px] truncate">{i.campeonato_descricao ?? '—'}</td>
                      <td className="px-4 py-3 text-compumaq-texto">{i.modalidade_nome ?? '—'}</td>
                      <td className="px-4 py-3 text-compumaq-textoSuave text-xs">{i.etapa_titulo ?? '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${i.categoria === 'dama' ? 'bg-pink-100 text-pink-700' : 'bg-compumaq-dourado/15 text-compumaq-doradoClaro'}`}>
                          {i.categoria}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-compumaq-textoSuave text-center font-mono">#{i.numero_inscricao}</td>
                      <td className="px-4 py-3 text-compumaq-texto font-medium">{formatCurrency(i.valor_cobrado)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[i.status] ?? ''}`}>
                          {i.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => imprimirComprovanteIndividual(i)}
                            className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium whitespace-nowrap"
                          >
                            Reimprimir
                          </button>
                          <button
                            onClick={() => imprimirComprovanteThermal(i)}
                            className="text-xs text-compumaq-textoSuave hover:text-compumaq-texto font-medium whitespace-nowrap"
                            title="Imprimir na impressora Bluetooth (termica)"
                          >
                            Termica
                          </button>
                          {i.status === 'pendente_pagamento' && (
                            <button
                              onClick={() => { setConfirmPagamentoTarget(i); setConfirmPagamentoForma('pix') }}
                              className="text-xs text-green-600 hover:text-green-800 font-medium whitespace-nowrap"
                            >
                              Confirmar Pag.
                            </button>
                          )}
                          {i.status !== 'cancelada' && i.status !== 'finalizada' && (
                            <button
                              onClick={() => {
                                setTrocarTarget(i)
                                setTrocarModalidade('')
                                setTrocarEtapa('')
                              }}
                              className="text-xs text-amber-600 hover:text-amber-800 font-medium whitespace-nowrap"
                            >
                              Trocar Mod.
                            </button>
                          )}
                          {i.status === 'ativa' && (
                            <button
                              onClick={() => setCancelTarget(i)}
                              className="text-xs text-red-600 hover:text-red-800 font-medium"
                            >
                              Cancelar
                            </button>
                          )}
                          {i.forma_pagamento === 'isento' && (
                            <button
                              onClick={async () => {
                                if (!window.confirm(`Excluir definitivamente esta inscricao isenta?\n\n${i.atirador_nome}\n${i.modalidade_nome} - ${i.etapa_titulo}\n\nEsta acao tambem remove o resultado lancado (se houver).`)) return
                                try {
                                  await excluirMutation.mutateAsync(i.idinscricao)
                                  setAlert({ type: 'success', message: 'Inscricao excluida com sucesso!' })
                                } catch (err: any) {
                                  setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao excluir inscricao' })
                                }
                              }}
                              className="text-xs text-red-700 hover:text-red-900 font-bold"
                              title="Excluir inscricao isenta"
                            >
                              Excluir
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {meta && meta.totalPages > 1 && (
              <Pagination page={page} totalPages={meta.totalPages} total={meta.total} limit={20} onPageChange={setPage} />
            )}
          </>
        )}
      </div>

      {/* ── Modal: Nova Inscrição ──────────────────────────────────────────── */}
      <Modal open={modalOpen} onClose={closeModal} title="Nova Inscricao" size="lg">
        <div className="space-y-5">

          {/* ── STEP: FORM ──────────────────────────────────────────────────── */}
          {modalStep === 'form' && (
            <>
              {/* 1. Campeonato + Etapa */}
              <div>
                <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Selecionar contexto</h3>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-compumaq-texto mb-1">Campeonato <span className="text-red-500">*</span></label>
                    <select
                      className={selectClass}
                      value={selCampeonato}
                      onChange={e => { setSelCampeonato(e.target.value ? parseInt(e.target.value) : ''); setSelNumeroEtapa(null) }}
                    >
                      <option value="">Selecione...</option>
                      {campeonatosData?.data?.map(c => (
                        <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
                      ))}
                    </select>
                  </div>

                  {/* Etapa (rounds) */}
                  {selCampeonato !== '' && (
                    <div>
                      <label className="block text-xs font-medium text-compumaq-texto mb-1">
                        Etapa <span className="text-red-500">*</span>
                      </label>
                      {loadingDados ? (
                        <div className="flex items-center gap-2 text-sm text-compumaq-textoSuave py-2">
                          <Spinner size="sm" /><span>Carregando etapas...</span>
                        </div>
                      ) : etapaRounds.length === 0 ? (
                        <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          Nenhuma etapa com inscricoes abertas.
                        </p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2">
                          {etapaRounds.map(round => (
                            <button
                              key={round.numero_etapa}
                              type="button"
                              onClick={() => setSelNumeroEtapa(round.numero_etapa)}
                              className={`p-2.5 rounded-lg border text-sm text-center transition-all ${
                                selNumeroEtapa === round.numero_etapa
                                  ? 'bg-compumaq-dourado/10 border-compumaq-dourado/40 text-compumaq-doradoClaro font-semibold'
                                  : 'bg-compumaq-preto border-compumaq-dourado/25 text-compumaq-textoSuave hover:bg-compumaq-pretoSuave'
                              }`}
                            >
                              <p className="font-semibold">{round.titulo}</p>
                              <p className="text-xs opacity-70 mt-0.5">
                                {round.modalidades_abertas} mod.
                              </p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* 2. Atirador autocomplete */}
              <div>
                <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Atirador</h3>
                <div className="relative" ref={autocompleteRef}>
                  <label className="block text-xs font-medium text-compumaq-texto mb-1">Buscar por nome ou CPF <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      className={inputClass}
                      placeholder="Digite nome ou CPF..."
                      value={atiradorSearch}
                      onChange={e => {
                        setAtiradorSearch(e.target.value)
                        if (selectedAtirador && e.target.value !== selectedAtirador.nome) {
                          setSelectedAtirador(null)
                          setSelArma('')
                        }
                      }}
                      onFocus={() => atiradorSuggestions.length > 0 && setShowSuggestions(true)}
                    />
                    {atiradorLoading && (
                      <div className="absolute right-2 top-2">
                        <Spinner size="sm" />
                      </div>
                    )}
                  </div>

                  {showSuggestions && atiradorSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-compumaq-pretoSuave border border-compumaq-dourado/25 rounded-lg shadow-lg overflow-hidden">
                      {atiradorSuggestions.map(a => (
                        <button
                          key={a.idatirador}
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-compumaq-dourado/10 transition-colors border-b border-compumaq-dourado/25 last:border-0"
                          onClick={() => selectAtirador(a)}
                        >
                          <p className="font-medium text-compumaq-texto text-sm">{a.nome}</p>
                          <p className="text-xs text-compumaq-textoSuave">CPF: {a.cpf} · Nasc: {formatDate(a.data_nascimento)} · Clube: {a.clube_nome ?? '—'}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selectedAtirador && (
                  <div className="mt-2 p-3 bg-compumaq-dourado/10 border border-compumaq-dourado/25 rounded-lg text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div><span className="text-xs text-compumaq-textoSuave">Nome:</span> <span className="font-medium">{selectedAtirador.nome}</span></div>
                      <div><span className="text-xs text-compumaq-textoSuave">CPF:</span> <span className="font-mono">{selectedAtirador.cpf}</span></div>
                      <div><span className="text-xs text-compumaq-textoSuave">Nasc.:</span> <span>{formatDate(selectedAtirador.data_nascimento)}</span></div>
                      <div><span className="text-xs text-compumaq-textoSuave">Clube:</span> <span>{selectedAtirador.clube_nome ?? '—'}</span></div>
                    </div>
                  </div>
                )}
              </div>

              {/* 3. Grid de TODAS as modalidades com quantidade */}
              {selNumeroEtapa !== null && (
                <div>
                  <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">
                    Modalidades — Etapa {selNumeroEtapa}
                  </h3>

                  {/* Categoria */}
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-compumaq-texto mb-1">Categoria</label>
                    <select
                      className={selectClass}
                      value={categoria}
                      onChange={e => setCategoria(e.target.value as 'overall' | 'dama')}
                    >
                      <option value="overall">Overall</option>
                      <option value="dama">Dama</option>
                    </select>
                  </div>

                  {/* Tabela de preços */}
                  {regraValores && regraValores.regras.length > 0 && (
                    <p className="text-xs text-compumaq-textoSuave mb-2">
                      1ª inscricao: {formatCurrency(calcularValorInscricao(1, regraValores.regras))}
                      {regraValores.numero_max_inscricao > 1 && (
                        <span> · 2ª+: {formatCurrency(calcularValorInscricao(2, regraValores.regras))} cada</span>
                      )}
                    </p>
                  )}

                  {modalidadesComEtapa.length === 0 ? (
                    <p className="text-sm text-compumaq-textoSuave bg-compumaq-preto border border-compumaq-dourado/25 rounded-lg px-3 py-4 text-center">
                      Nenhuma modalidade disponivel para esta etapa.
                    </p>
                  ) : (
                    <div className="border border-compumaq-dourado/25 rounded-lg overflow-hidden">
                      <div className="bg-compumaq-preto px-4 py-2 border-b border-compumaq-dourado/25">
                        <div className="flex justify-between items-center">
                          <p className="text-xs font-medium text-compumaq-textoSuave uppercase">Modalidades disponíveis</p>
                          {totalGeral > 0 && (
                            <p className="text-sm font-bold text-compumaq-texto">Total: {formatCurrency(totalGeral)}</p>
                          )}
                        </div>
                      </div>
                      <div className="divide-y divide-compumaq-dourado/25">
                        {modalidadesComEtapa.map(m => {
                          const qty = quantidades[m.idmodalidade] ?? 0
                          const maxQty = getMaxQty(m)
                          const subtotal = subtotaisGlobais[m.idmodalidade] ?? 0
                          return (
                            <div
                              key={m.idmodalidade}
                              className={`px-4 py-3 flex items-center justify-between transition-colors ${
                                qty > 0 ? 'bg-compumaq-dourado/10' : ''
                              }`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium text-sm ${qty > 0 ? 'text-compumaq-doradoClaro' : 'text-compumaq-texto'}`}>
                                  {m.nome}
                                </p>
                                {m.divisao && (
                                  <p className="text-xs text-compumaq-textoSuave">{m.divisao}</p>
                                )}
                                {maxQty > 1 && (
                                  <p className="text-xs text-green-600">Reinscricao permitida (max {maxQty}x)</p>
                                )}
                              </div>
                              <div className="flex items-center gap-3 shrink-0 ml-2">
                                {qty > 0 && (
                                  <span className="text-sm font-semibold text-green-700 min-w-[64px] text-right">
                                    {formatCurrency(subtotal)}
                                  </span>
                                )}
                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => adjustQty(m.idmodalidade, -1, maxQty)}
                                    disabled={qty === 0}
                                    className="w-7 h-7 flex items-center justify-center border border-compumaq-dourado/25 rounded-md text-compumaq-textoSuave hover:bg-compumaq-pretoSuave disabled:opacity-40 text-base font-bold"
                                  >
                                    −
                                  </button>
                                  <span className="w-8 text-center font-semibold text-compumaq-texto text-sm">{qty}</span>
                                  <button
                                    type="button"
                                    onClick={() => adjustQty(m.idmodalidade, +1, maxQty)}
                                    disabled={qty >= maxQty}
                                    className="w-7 h-7 flex items-center justify-center border border-compumaq-dourado/25 rounded-md text-compumaq-textoSuave hover:bg-compumaq-pretoSuave disabled:opacity-40 text-base font-bold"
                                  >
                                    +
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                      {totalGeral > 0 && (
                        <div className="px-4 py-3 bg-compumaq-preto border-t border-compumaq-dourado/25 flex justify-between items-center">
                          <span className="text-sm font-semibold text-compumaq-texto">Total Geral</span>
                          <span className="text-lg font-bold text-compumaq-texto">{formatCurrency(totalGeral)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 4. Arma (condicional) */}
              {campeonatoSelecionado?.flag_obriga_arma_inscricao && selectedAtirador && (
                <div>
                  <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">
                    Arma <span className="text-red-500">*</span> <span className="text-red-400 text-xs normal-case">(obrigatoria)</span>
                  </h3>
                  {armas.length === 0 ? (
                    <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                      Atirador nao possui armas cadastradas. Cadastre uma arma antes de inscrever.
                    </p>
                  ) : (
                    <select
                      className={selectClass}
                      value={selArma}
                      onChange={e => setSelArma(e.target.value ? parseInt(e.target.value) : '')}
                    >
                      <option value="">Selecione uma arma...</option>
                      {armas.map(a => (
                        <option key={a.idarma} value={a.idarma}>
                          {a.marca} {a.modelo} — {a.calibre} ({a.tipo})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {campeonatoSelecionado && !campeonatoSelecionado.flag_obriga_arma_inscricao && selectedAtirador && armas.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-compumaq-texto mb-1">Arma (opcional)</label>
                  <select
                    className={selectClass}
                    value={selArma}
                    onChange={e => setSelArma(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">Sem arma / selecione...</option>
                    {armas.map(a => (
                      <option key={a.idarma} value={a.idarma}>
                        {a.marca} {a.modelo} — {a.calibre} ({a.tipo})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Botoes */}
              <div className="flex justify-end gap-3 pt-2 border-t border-compumaq-dourado/25">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAvancarPagamento}
                  disabled={!podeAvancar}
                  className="px-6 py-2 bg-compumaq-dourado text-compumaq-texto text-sm rounded-lg hover:bg-compumaq-doradoClaro disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  Finalizar Inscricao
                </button>
              </div>
            </>
          )}

          {/* ── STEP: PAGAMENTO ──────────────────────────────────────────────── */}
          {modalStep === 'payment' && (
            <>
              {/* Resumo */}
              <div className="bg-compumaq-preto border border-compumaq-dourado/25 rounded-lg p-4">
                <p className="text-xs font-semibold text-compumaq-textoSuave uppercase mb-2">Resumo</p>
                <p className="text-sm font-medium text-compumaq-texto">{selectedAtirador?.nome}</p>
                <p className="text-xs text-compumaq-textoSuave">{campeonatoSelecionado?.descricao} — Etapa {selNumeroEtapa}</p>
                <div className="mt-2 space-y-1">
                  {modalidadesComEtapa
                    .filter(m => (quantidades[m.idmodalidade] ?? 0) > 0)
                    .map(m => (
                      <div key={m.idmodalidade} className="flex justify-between text-xs text-compumaq-textoSuave">
                        <span>{m.nome} × {quantidades[m.idmodalidade]}</span>
                        <span className="font-medium">{formatCurrency(subtotaisGlobais[m.idmodalidade] ?? 0)}</span>
                      </div>
                    ))}
                </div>
                <div className="mt-2 pt-2 border-t border-compumaq-dourado/25 flex justify-between items-center">
                  <span className="text-sm font-bold text-compumaq-texto">Total</span>
                  <span className="text-base font-bold text-compumaq-texto">{formatCurrency(totalGeral)}</span>
                </div>
              </div>

              {/* Opcoes de pagamento */}
              <div>
                <h3 className="text-xs font-semibold text-compumaq-textoSuave uppercase tracking-wide mb-3">Forma de Pagamento</h3>

                {paymentMode === 'none' && (
                  <div className="grid grid-cols-2 gap-3">
                    {/* PIX */}
                    <button
                      onClick={() => setPaymentMode('pix')}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border border-compumaq-dourado/25 bg-compumaq-preto hover:bg-green-50 hover:border-green-400 transition-all"
                    >
                      <span className="text-3xl">⚡</span>
                      <span className="text-compumaq-texto font-semibold text-sm">PIX</span>
                      <span className="text-compumaq-textoSuave text-xs">Registrar com PIX</span>
                    </button>
                    {/* POS */}
                    <button
                      onClick={() => setPaymentMode('pos')}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border border-compumaq-dourado/25 bg-compumaq-preto hover:bg-purple-50 hover:border-purple-400 transition-all"
                    >
                      <span className="text-3xl">📟</span>
                      <span className="text-compumaq-texto font-semibold text-sm">Cartao POS</span>
                      <span className="text-compumaq-textoSuave text-xs">Maquina no local</span>
                    </button>
                    {/* Isento */}
                    <button
                      onClick={() => setPaymentMode('isento')}
                      className="flex flex-col items-center gap-2 p-4 rounded-xl border border-compumaq-dourado/25 bg-compumaq-preto hover:bg-orange-50 hover:border-orange-400 transition-all"
                    >
                      <span className="text-3xl">🔓</span>
                      <span className="text-compumaq-texto font-semibold text-sm">Isento</span>
                      <span className="text-compumaq-textoSuave text-xs">Sem cobranca</span>
                    </button>
                    {/* Cartão Online — info only */}
                    <div className="flex flex-col items-center gap-2 p-4 rounded-xl border border-compumaq-dourado/25 bg-compumaq-preto opacity-50 cursor-not-allowed">
                      <span className="text-3xl">💳</span>
                      <span className="text-compumaq-textoSuave font-semibold text-sm">Cartao Online</span>
                      <span className="text-compumaq-textoSuave text-xs">Via pagina publica</span>
                    </div>
                  </div>
                )}

                {/* PIX: confirmar e registrar */}
                {paymentMode === 'pix' && (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <p className="text-2xl mb-2">⚡</p>
                      <p className="font-semibold text-compumaq-texto">Registrar inscricao com PIX</p>
                      <p className="text-sm text-compumaq-textoSuave mt-1">A inscricao sera registrada como pendente de pagamento.</p>
                      <p className="text-2xl font-bold text-compumaq-texto mt-3">{formatCurrency(totalGeral)}</p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setPaymentMode('none')}
                        className="flex-none px-4 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                      >
                        Voltar
                      </button>
                      <button
                        onClick={() => handleFinalizar('pix')}
                        disabled={submitting}
                        className="flex-1 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
                      >
                        {submitting ? 'Inscrevendo...' : 'Confirmar Inscricao (PIX)'}
                      </button>
                    </div>
                  </div>
                )}

                {/* POS: confirmar */}
                {paymentMode === 'pos' && (
                  <div className="space-y-4">
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                      <p className="text-2xl mb-2">📟</p>
                      <p className="font-semibold text-compumaq-texto">Pagamento na maquina POS</p>
                      <p className="text-sm text-compumaq-textoSuave mt-1">Inscricao confirmada apos pagamento na maquina.</p>
                      <p className="text-2xl font-bold text-compumaq-texto mt-3">{formatCurrency(totalGeral)}</p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setPaymentMode('none')}
                        className="flex-none px-4 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                      >
                        Voltar
                      </button>
                      <button
                        onClick={() => handleFinalizar('pos')}
                        disabled={submitting}
                        className="flex-1 py-2.5 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {submitting ? 'Inscrevendo...' : 'Confirmar Inscricao (POS)'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Isento: confirmar */}
                {paymentMode === 'isento' && (
                  <div className="space-y-4">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                      <p className="text-2xl mb-2">🔓</p>
                      <p className="font-semibold text-compumaq-texto">Inscricao Isenta</p>
                      <p className="text-sm text-compumaq-textoSuave mt-1">O atirador nao sera cobrado.</p>
                      <p className="text-2xl font-bold text-orange-600 mt-3">R$ 0,00</p>
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setPaymentMode('none')}
                        className="flex-none px-4 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                      >
                        Voltar
                      </button>
                      <button
                        onClick={() => handleFinalizar('isento')}
                        disabled={submitting}
                        className="flex-1 py-2.5 bg-orange-600 text-white text-sm font-semibold rounded-lg hover:bg-orange-700 disabled:opacity-50"
                      >
                        {submitting ? 'Inscrevendo...' : 'Confirmar Isencao'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {paymentMode === 'none' && (
                <div className="flex justify-start pt-2 border-t border-compumaq-dourado/25">
                  <button
                    type="button"
                    onClick={() => setModalStep('form')}
                    className="px-4 py-2 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                  >
                    Voltar
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── STEP: SUCESSO ────────────────────────────────────────────────── */}
          {modalStep === 'sucesso' && batchResult && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto text-3xl">
                ✓
              </div>
              <div>
                <p className="text-xl font-bold text-green-700">Inscricao Realizada!</p>
                <p className="text-sm text-compumaq-textoSuave mt-1">
                  {paymentMode === 'pos' && 'Inscricao confirmada via POS.'}
                  {paymentMode === 'isento' && 'Inscricao isenta confirmada.'}
                  {paymentMode === 'pix' && 'Inscricao registrada — aguardando confirmacao de pagamento.'}
                </p>
              </div>

              <div className="text-left bg-compumaq-preto border border-compumaq-dourado/25 rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold text-compumaq-textoSuave uppercase mb-1">Detalhes</p>
                <p className="text-sm text-compumaq-texto"><span className="font-medium">Atirador:</span> {selectedAtirador?.nome}</p>
                <p className="text-sm text-compumaq-texto"><span className="font-medium">Campeonato:</span> {campeonatoSelecionado?.descricao}</p>
                <p className="text-sm text-compumaq-texto"><span className="font-medium">Etapa:</span> {selNumeroEtapa}</p>
                <div className="pt-2 border-t border-compumaq-dourado/25 space-y-1">
                  {batchResult.inscricoes.map((insc, idx) => (
                    <div key={idx} className="flex justify-between text-sm text-compumaq-textoSuave">
                      <span>{insc.modalidade_nome} × {insc.quantidade}</span>
                      <span className="font-medium text-green-700">{formatCurrency(insc.subtotal)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm font-bold text-compumaq-texto pt-1 border-t border-compumaq-dourado/25">
                    <span>Total</span>
                    <span>{formatCurrency(batchResult.total)}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={closeModal}
                  className="flex-1 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
                >
                  Fechar
                </button>
                <button
                  onClick={imprimirComprovante}
                  className="flex-1 py-2.5 bg-compumaq-dourado text-compumaq-texto text-sm font-semibold rounded-lg hover:bg-compumaq-doradoClaro"
                >
                  Salvar e Imprimir
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Confirmar cancelamento */}
      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelar}
        title="Cancelar Inscricao"
        message={`Cancelar a inscricao #${cancelTarget?.numero_inscricao} de "${cancelTarget?.atirador_nome}" na modalidade "${cancelTarget?.modalidade_nome}"?`}
        loading={cancelarInscricao.isPending}
      />

      {/* Modal: Confirmar Pagamento */}
      <Modal
        open={!!confirmPagamentoTarget}
        onClose={() => { setConfirmPagamentoTarget(null); setConfirmPagamentoForma('pix') }}
        title="Confirmar Pagamento"
        size="sm"
      >
        {confirmPagamentoTarget && (
          <div className="space-y-4">
            <div className="bg-compumaq-preto rounded-lg p-3">
              <p className="text-xs text-compumaq-textoSuave mb-0.5">Atirador</p>
              <p className="font-semibold text-compumaq-texto">{confirmPagamentoTarget.atirador_nome}</p>
              <p className="text-xs text-compumaq-textoSuave mt-1">{confirmPagamentoTarget.modalidade_nome} — Insc. #{confirmPagamentoTarget.numero_inscricao}</p>
              <p className="text-sm font-bold text-green-700 mt-1">{formatCurrency(confirmPagamentoTarget.valor_cobrado)}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-compumaq-texto mb-2">Forma de Pagamento</label>
              <div className="grid grid-cols-2 gap-2">
                {(['pix', 'cartao', 'pos', 'isento'] as const).map(forma => (
                  <button
                    key={forma}
                    type="button"
                    onClick={() => setConfirmPagamentoForma(forma)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all ${
                      confirmPagamentoForma === forma
                        ? 'border-compumaq-dourado bg-compumaq-dourado/10 text-compumaq-doradoClaro'
                        : 'border-compumaq-dourado/25 bg-compumaq-pretoSuave text-compumaq-texto hover:bg-compumaq-preto'
                    }`}
                  >
                    <span>{forma === 'pix' ? '⚡' : forma === 'cartao' ? '💳' : forma === 'pos' ? '📟' : '🔓'}</span>
                    <span className="capitalize">{forma === 'pos' ? 'Maquina (POS)' : forma === 'isento' ? 'Isento' : forma === 'cartao' ? 'Cartao' : 'PIX'}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => { setConfirmPagamentoTarget(null); setConfirmPagamentoForma('pix') }}
                className="flex-1 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmarPagamento}
                disabled={confirmPagamentoLoading}
                className="flex-1 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {confirmPagamentoLoading ? 'Confirmando...' : 'Confirmar Pagamento'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal: Trocar Modalidade */}
      <Modal
        open={!!trocarTarget}
        onClose={() => { setTrocarTarget(null); setTrocarModalidade(''); setTrocarEtapa('') }}
        title="Trocar Modalidade"
        size="sm"
      >
        {trocarTarget && (
          <div className="space-y-4">
            <div className="bg-compumaq-preto rounded-lg p-3">
              <p className="text-xs text-compumaq-textoSuave mb-0.5">Atirador</p>
              <p className="font-semibold text-compumaq-texto">{trocarTarget.atirador_nome}</p>
              <p className="text-xs text-compumaq-textoSuave mt-1">Atual: {trocarTarget.modalidade_nome} — {trocarTarget.etapa_titulo}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-compumaq-texto mb-1">Nova Modalidade</label>
              <select
                className={selectClass}
                value={trocarModalidade}
                onChange={e => {
                  setTrocarModalidade(e.target.value ? parseInt(e.target.value) : '')
                  setTrocarEtapa('')
                }}
              >
                <option value="">Selecione...</option>
                {modalidadesTroca.map(m => (
                  <option key={m.idmodalidade} value={m.idmodalidade}>
                    {m.nome}{m.divisao ? ` — ${m.divisao}` : ''} ({m.grupo})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-compumaq-texto mb-1">Nova Etapa</label>
              <select
                className={selectClass}
                value={trocarEtapa}
                onChange={e => setTrocarEtapa(e.target.value ? parseInt(e.target.value) : '')}
                disabled={!trocarModalidade}
              >
                <option value="">{trocarModalidade ? 'Selecione...' : '— selecione a modalidade —'}</option>
                {etapasTroca.map(e => (
                  <option key={e.idetapa} value={e.idetapa}>{e.titulo_reduzido || `Etapa ${e.numero_etapa}`}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => { setTrocarTarget(null); setTrocarModalidade(''); setTrocarEtapa('') }}
                className="flex-1 py-2.5 border border-compumaq-dourado/25 text-sm rounded-lg hover:bg-compumaq-preto"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!trocarTarget || !trocarModalidade || !trocarEtapa) return
                  try {
                    await trocarModalidadeMutation.mutateAsync({
                      id: trocarTarget.idinscricao,
                      data: { idmodalidade: trocarModalidade as number, idetapa: trocarEtapa as number },
                    })
                    setAlert({ type: 'success', message: 'Modalidade trocada com sucesso!' })
                    setTrocarTarget(null)
                    setTrocarModalidade('')
                    setTrocarEtapa('')
                  } catch (err: any) {
                    setAlert({ type: 'error', message: err.response?.data?.error ?? 'Erro ao trocar modalidade' })
                  }
                }}
                disabled={trocarModalidadeMutation.isPending || !trocarModalidade || !trocarEtapa}
                className="flex-1 py-2.5 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {trocarModalidadeMutation.isPending ? 'Trocando...' : 'Confirmar Troca'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
